import { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Self-contained, fully localized labels for every supported app language.
const L = {
  tr: {
    intro: "AI öğretmeniniz Emma ile daha doğal ve kişisel sohbetler yapabilmeniz için aşağıdaki bilgileri rica ediyoruz. Bu bilgiler yalnızca derslerinizi kişiselleştirmek için kullanılır ve gizlilik politikamıza uygun olarak saklanır.",
    nationality: "Uyruğunuz", nationality_ph: "Örn. Türk",
    country: "Yaşadığınız ülke", country_ph: "Örn. Türkiye",
    age: "Yaş aralığınız", occupation: "Durumunuz",
    occ_student: "Öğrenci", occ_working: "Çalışıyorum", occ_homemaker: "Ev hanımı / Ev erkeği", occ_retired: "Emekli", occ_not_working: "Çalışmıyorum",
    job: "Mesleğiniz", job_ph: "Örn. Doktor, Öğretmen, Mühendis",
    marital: "Medeni durumunuz", married: "Evli", single: "Bekar",
    children: "Çocuğunuz var mı?", yes: "Evet", no: "Hayır",
    gender: "Cinsiyetiniz", male: "Erkek", female: "Kadın",
    select: "Seçiniz", complete: "Tamamla ve Başla", save: "Kaydet",
    saved: "Profiliniz kaydedildi", save_failed: "Profil kaydedilemedi",
  },
  en: {
    intro: "To help your AI teacher Emma have more natural and personal conversations with you, please share the information below. It is used only to personalize your lessons and is stored in line with our privacy policy.",
    nationality: "Your nationality", nationality_ph: "e.g. Turkish",
    country: "Country you live in", country_ph: "e.g. Turkey",
    age: "Your age range", occupation: "Your status",
    occ_student: "Student", occ_working: "Working", occ_homemaker: "Homemaker", occ_retired: "Retired", occ_not_working: "Not working",
    job: "Your profession", job_ph: "e.g. Doctor, Teacher, Engineer",
    marital: "Marital status", married: "Married", single: "Single",
    children: "Do you have children?", yes: "Yes", no: "No",
    gender: "Gender", male: "Male", female: "Female",
    select: "Select", complete: "Finish & Start", save: "Save",
    saved: "Your profile has been saved", save_failed: "Could not save profile",
  },
  de: {
    intro: "Damit deine KI-Lehrerin Emma natürlichere und persönlichere Gespräche mit dir führen kann, bitten wir dich um die folgenden Angaben. Sie werden nur zur Personalisierung deiner Lektionen verwendet und gemäß unserer Datenschutzrichtlinie gespeichert.",
    nationality: "Deine Nationalität", nationality_ph: "z. B. Türkisch",
    country: "Land, in dem du lebst", country_ph: "z. B. Türkei",
    age: "Deine Altersgruppe", occupation: "Dein Status",
    occ_student: "Schüler/Student", occ_working: "Berufstätig", occ_homemaker: "Hausfrau/Hausmann", occ_retired: "Rentner", occ_not_working: "Nicht berufstätig",
    job: "Dein Beruf", job_ph: "z. B. Arzt, Lehrer, Ingenieur",
    marital: "Familienstand", married: "Verheiratet", single: "Ledig",
    children: "Hast du Kinder?", yes: "Ja", no: "Nein",
    gender: "Geschlecht", male: "Männlich", female: "Weiblich",
    select: "Auswählen", complete: "Fertig & Starten", save: "Speichern",
    saved: "Dein Profil wurde gespeichert", save_failed: "Profil konnte nicht gespeichert werden",
  },
  it: {
    intro: "Per permettere alla tua insegnante AI Emma di avere conversazioni più naturali e personali con te, ti chiediamo le seguenti informazioni. Vengono usate solo per personalizzare le tue lezioni e conservate nel rispetto della nostra privacy policy.",
    nationality: "La tua nazionalità", nationality_ph: "es. Turca",
    country: "Paese in cui vivi", country_ph: "es. Turchia",
    age: "La tua fascia d'età", occupation: "La tua situazione",
    occ_student: "Studente", occ_working: "Lavoro", occ_homemaker: "Casalingo/a", occ_retired: "Pensionato/a", occ_not_working: "Non lavoro",
    job: "La tua professione", job_ph: "es. Medico, Insegnante, Ingegnere",
    marital: "Stato civile", married: "Sposato/a", single: "Single",
    children: "Hai figli?", yes: "Sì", no: "No",
    gender: "Sesso", male: "Maschio", female: "Femmina",
    select: "Seleziona", complete: "Completa e Inizia", save: "Salva",
    saved: "Il tuo profilo è stato salvato", save_failed: "Impossibile salvare il profilo",
  },
  es: {
    intro: "Para que tu profesora de IA Emma tenga conversaciones más naturales y personales contigo, te pedimos la siguiente información. Se usa solo para personalizar tus lecciones y se guarda conforme a nuestra política de privacidad.",
    nationality: "Tu nacionalidad", nationality_ph: "p. ej. Turca",
    country: "País en el que vives", country_ph: "p. ej. Turquía",
    age: "Tu rango de edad", occupation: "Tu situación",
    occ_student: "Estudiante", occ_working: "Trabajo", occ_homemaker: "Ama/o de casa", occ_retired: "Jubilado/a", occ_not_working: "No trabajo",
    job: "Tu profesión", job_ph: "p. ej. Médico, Profesor, Ingeniero",
    marital: "Estado civil", married: "Casado/a", single: "Soltero/a",
    children: "¿Tienes hijos?", yes: "Sí", no: "No",
    gender: "Sexo", male: "Hombre", female: "Mujer",
    select: "Seleccionar", complete: "Finalizar y Empezar", save: "Guardar",
    saved: "Tu perfil se ha guardado", save_failed: "No se pudo guardar el perfil",
  },
  fr: {
    intro: "Pour que ton enseignante IA Emma puisse avoir des conversations plus naturelles et personnelles avec toi, nous te demandons les informations suivantes. Elles servent uniquement à personnaliser tes leçons et sont conservées conformément à notre politique de confidentialité.",
    nationality: "Ta nationalité", nationality_ph: "ex. Turque",
    country: "Pays où tu vis", country_ph: "ex. Turquie",
    age: "Ta tranche d'âge", occupation: "Ta situation",
    occ_student: "Étudiant", occ_working: "En activité", occ_homemaker: "Au foyer", occ_retired: "Retraité", occ_not_working: "Sans activité",
    job: "Ta profession", job_ph: "ex. Médecin, Enseignant, Ingénieur",
    marital: "Situation familiale", married: "Marié(e)", single: "Célibataire",
    children: "As-tu des enfants ?", yes: "Oui", no: "Non",
    gender: "Sexe", male: "Homme", female: "Femme",
    select: "Sélectionner", complete: "Terminer et Commencer", save: "Enregistrer",
    saved: "Ton profil a été enregistré", save_failed: "Impossible d'enregistrer le profil",
  },
  ru: {
    intro: "Чтобы ваш ИИ-преподаватель Эмма могла вести с вами более естественные и личные беседы, просим заполнить данные ниже. Они используются только для персонализации уроков и хранятся в соответствии с нашей политикой конфиденциальности.",
    nationality: "Ваша национальность", nationality_ph: "напр. турок",
    country: "Страна проживания", country_ph: "напр. Турция",
    age: "Ваш возрастной диапазон", occupation: "Ваш статус",
    occ_student: "Студент", occ_working: "Работаю", occ_homemaker: "Домохозяйка/домохозяин", occ_retired: "На пенсии", occ_not_working: "Не работаю",
    job: "Ваша профессия", job_ph: "напр. врач, учитель, инженер",
    marital: "Семейное положение", married: "В браке", single: "Не в браке",
    children: "У вас есть дети?", yes: "Да", no: "Нет",
    gender: "Пол", male: "Мужской", female: "Женский",
    select: "Выбрать", complete: "Завершить и начать", save: "Сохранить",
    saved: "Ваш профиль сохранён", save_failed: "Не удалось сохранить профиль",
  },
  cnr: {
    intro: "Da bi tvoja AI nastavnica Emma mogla voditi prirodnije i ličnije razgovore s tobom, molimo te za sljedeće podatke. Koriste se samo za personalizaciju lekcija i čuvaju u skladu s našom politikom privatnosti.",
    nationality: "Tvoja nacionalnost", nationality_ph: "npr. Turčin",
    country: "Država u kojoj živiš", country_ph: "npr. Turska",
    age: "Tvoja starosna grupa", occupation: "Tvoj status",
    occ_student: "Učenik/Student", occ_working: "Zaposlen/a", occ_homemaker: "Domaćica/domaćin", occ_retired: "Penzioner", occ_not_working: "Ne radim",
    job: "Tvoje zanimanje", job_ph: "npr. ljekar, nastavnik, inženjer",
    marital: "Bračni status", married: "U braku", single: "Slobodan/Slobodna",
    children: "Imaš li djece?", yes: "Da", no: "Ne",
    gender: "Pol", male: "Muški", female: "Ženski",
    select: "Izaberi", complete: "Završi i počni", save: "Sačuvaj",
    saved: "Tvoj profil je sačuvan", save_failed: "Nije moguće sačuvati profil",
  },
  sr: {
    intro: "Da bi tvoja AI nastavnica Emma mogla da vodi prirodnije i ličnije razgovore s tobom, molimo te za sledeće podatke. Koriste se samo za personalizaciju lekcija i čuvaju se u skladu sa našom politikom privatnosti.",
    nationality: "Tvoja nacionalnost", nationality_ph: "npr. Turčin",
    country: "Država u kojoj živiš", country_ph: "npr. Turska",
    age: "Tvoja starosna grupa", occupation: "Tvoj status",
    occ_student: "Učenik/Student", occ_working: "Zaposlen/a", occ_homemaker: "Domaćica/domaćin", occ_retired: "Penzioner", occ_not_working: "Ne radim",
    job: "Tvoje zanimanje", job_ph: "npr. lekar, nastavnik, inženjer",
    marital: "Bračni status", married: "U braku", single: "Slobodan/Slobodna",
    children: "Da li imaš decu?", yes: "Da", no: "Ne",
    gender: "Pol", male: "Muški", female: "Ženski",
    select: "Izaberi", complete: "Završi i počni", save: "Sačuvaj",
    saved: "Tvoj profil je sačuvan", save_failed: "Nije moguće sačuvati profil",
  },
  sq: {
    intro: "Që mësuesja jote me AI, Emma, të bëjë biseda më të natyrshme dhe personale me ty, të kërkojmë informacionet e mëposhtme. Përdoren vetëm për të personalizuar mësimet dhe ruhen sipas politikës sonë të privatësisë.",
    nationality: "Kombësia jote", nationality_ph: "p.sh. Turk",
    country: "Shteti ku jeton", country_ph: "p.sh. Turqi",
    age: "Grupmosha jote", occupation: "Statusi yt",
    occ_student: "Student", occ_working: "I/E punësuar", occ_homemaker: "Amvise", occ_retired: "Në pension", occ_not_working: "Nuk punoj",
    job: "Profesioni yt", job_ph: "p.sh. Mjek, Mësues, Inxhinier",
    marital: "Gjendja civile", married: "I/E martuar", single: "Beqar/e",
    children: "A ke fëmijë?", yes: "Po", no: "Jo",
    gender: "Gjinia", male: "Mashkull", female: "Femër",
    select: "Zgjidh", complete: "Përfundo dhe Fillo", save: "Ruaj",
    saved: "Profili yt u ruajt", save_failed: "Profili nuk u ruajt dot",
  },
  el: {
    intro: "Για να μπορεί η δασκάλα σου με τεχνητή νοημοσύνη, η Emma, να κάνει πιο φυσικές και προσωπικές συζητήσεις μαζί σου, σου ζητάμε τις παρακάτω πληροφορίες. Χρησιμοποιούνται μόνο για την εξατομίκευση των μαθημάτων και φυλάσσονται σύμφωνα με την πολιτική απορρήτου μας.",
    nationality: "Η εθνικότητά σου", nationality_ph: "π.χ. Τούρκος",
    country: "Χώρα διαμονής", country_ph: "π.χ. Τουρκία",
    age: "Η ηλικιακή σου ομάδα", occupation: "Η κατάστασή σου",
    occ_student: "Μαθητής/Φοιτητής", occ_working: "Εργαζόμενος/η", occ_homemaker: "Οικιακά", occ_retired: "Συνταξιούχος", occ_not_working: "Δεν εργάζομαι",
    job: "Το επάγγελμά σου", job_ph: "π.χ. Γιατρός, Δάσκαλος, Μηχανικός",
    marital: "Οικογενειακή κατάσταση", married: "Παντρεμένος/η", single: "Ελεύθερος/η",
    children: "Έχεις παιδιά;", yes: "Ναι", no: "Όχι",
    gender: "Φύλο", male: "Άνδρας", female: "Γυναίκα",
    select: "Επίλεξε", complete: "Ολοκλήρωση & Έναρξη", save: "Αποθήκευση",
    saved: "Το προφίλ σου αποθηκεύτηκε", save_failed: "Δεν ήταν δυνατή η αποθήκευση του προφίλ",
  },
};

// Children-detail labels (shown only when "has children" = yes).
const LC = {
  tr: { children_count: "Kaç çocuğunuz var?", children_gender: "Çocuklarınızın cinsiyeti", cg_boy: "Erkek", cg_girl: "Kız", cg_both: "Her ikisi de" },
  en: { children_count: "How many children?", children_gender: "Children's gender", cg_boy: "Boy(s)", cg_girl: "Girl(s)", cg_both: "Both" },
  de: { children_count: "Wie viele Kinder?", children_gender: "Geschlecht der Kinder", cg_boy: "Junge(n)", cg_girl: "Mädchen", cg_both: "Beides" },
  it: { children_count: "Quanti figli?", children_gender: "Sesso dei figli", cg_boy: "Maschio/i", cg_girl: "Femmina/e", cg_both: "Entrambi" },
  es: { children_count: "¿Cuántos hijos?", children_gender: "Sexo de los hijos", cg_boy: "Niño(s)", cg_girl: "Niña(s)", cg_both: "Ambos" },
  fr: { children_count: "Combien d'enfants ?", children_gender: "Sexe des enfants", cg_boy: "Garçon(s)", cg_girl: "Fille(s)", cg_both: "Les deux" },
  ru: { children_count: "Сколько детей?", children_gender: "Пол детей", cg_boy: "Мальчик(и)", cg_girl: "Девочка(и)", cg_both: "Оба" },
  cnr: { children_count: "Koliko djece?", children_gender: "Pol djece", cg_boy: "Dječak(ci)", cg_girl: "Djevojčica(e)", cg_both: "Oboje" },
  sr: { children_count: "Koliko dece?", children_gender: "Pol dece", cg_boy: "Dečak(ci)", cg_girl: "Devojčica(e)", cg_both: "Oboje" },
  sq: { children_count: "Sa fëmijë?", children_gender: "Gjinia e fëmijëve", cg_boy: "Djalë/djem", cg_girl: "Vajzë/vajza", cg_both: "Të dyja" },
  el: { children_count: "Πόσα παιδιά;", children_gender: "Φύλο των παιδιών", cg_boy: "Αγόρι(α)", cg_girl: "Κορίτσι(α)", cg_both: "Και τα δύο" },
};

const AGE_RANGES = ["<18", "18-25", "26-40", "41-60", "60+"];
const CHILD_COUNTS = ["1", "2", "3", "4", "4+"];

export default function ProfileForm({ initialProfile = null, submitLabelKey = "complete", onSaved }) {
  const { getAuthHeader, refreshUser } = useAuth();
  const { language } = useTranslation();
  const lang = { ...(L[language] || L.en), ...(LC[language] || LC.en) };

  const [form, setForm] = useState({
    nationality: initialProfile?.nationality || "",
    country: initialProfile?.country || "",
    age_range: initialProfile?.age_range || "",
    occupation: initialProfile?.occupation || "",
    job: initialProfile?.job || "",
    marital_status: initialProfile?.marital_status || "",
    has_children: initialProfile?.has_children === true ? "yes" : initialProfile?.has_children === false ? "no" : "",
    children_count: initialProfile?.children_count || "",
    children_gender: initialProfile?.children_gender || "",
    gender: initialProfile?.gender || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const jobRequired = form.occupation === "working";
  const hasKids = form.has_children === "yes";

  const isValid =
    form.nationality.trim() &&
    form.country.trim() &&
    form.age_range &&
    form.occupation &&
    (!jobRequired || form.job.trim()) &&
    form.marital_status &&
    form.has_children &&
    (!hasKids || (form.children_count && form.children_gender)) &&
    form.gender;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || saving) return;
    setSaving(true);
    try {
      const payload = {
        nationality: form.nationality.trim(),
        country: form.country.trim(),
        age_range: form.age_range,
        occupation: form.occupation,
        job: jobRequired ? form.job.trim() : "",
        marital_status: form.marital_status,
        has_children: hasKids,
        children_count: hasKids ? form.children_count : "",
        children_gender: hasKids ? form.children_gender : "",
        gender: form.gender,
      };
      await axios.put(`${API}/auth/profile`, payload, getAuthHeader());
      await refreshUser();
      toast.success(lang.saved);
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || lang.save_failed);
    } finally {
      setSaving(false);
    }
  };

  const selectClass = "bg-neutral-800 border-neutral-700 text-white";
  const contentClass = "bg-neutral-800 border-neutral-700";
  const itemClass = "text-white hover:bg-neutral-700";

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="profile-form">
      {/* Nationality */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.nationality}</Label>
        <Input
          value={form.nationality}
          onChange={(e) => set("nationality", e.target.value)}
          placeholder={lang.nationality_ph}
          className={selectClass}
          data-testid="profile-nationality-input"
        />
      </div>

      {/* Country */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.country}</Label>
        <Input
          value={form.country}
          onChange={(e) => set("country", e.target.value)}
          placeholder={lang.country_ph}
          className={selectClass}
          data-testid="profile-country-input"
        />
      </div>

      {/* Age range */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.age}</Label>
        <Select value={form.age_range} onValueChange={(v) => set("age_range", v)}>
          <SelectTrigger className={selectClass} data-testid="profile-age-select">
            <SelectValue placeholder={lang.select} />
          </SelectTrigger>
          <SelectContent className={contentClass}>
            {AGE_RANGES.map((r) => (
              <SelectItem key={r} value={r} className={itemClass} data-testid={`profile-age-${r}`}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Occupation */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.occupation}</Label>
        <Select value={form.occupation} onValueChange={(v) => set("occupation", v)}>
          <SelectTrigger className={selectClass} data-testid="profile-occupation-select">
            <SelectValue placeholder={lang.select} />
          </SelectTrigger>
          <SelectContent className={contentClass}>
            <SelectItem value="student" className={itemClass} data-testid="profile-occ-student">{lang.occ_student}</SelectItem>
            <SelectItem value="working" className={itemClass} data-testid="profile-occ-working">{lang.occ_working}</SelectItem>
            <SelectItem value="homemaker" className={itemClass} data-testid="profile-occ-homemaker">{lang.occ_homemaker}</SelectItem>
            <SelectItem value="retired" className={itemClass} data-testid="profile-occ-retired">{lang.occ_retired}</SelectItem>
            <SelectItem value="not_working" className={itemClass} data-testid="profile-occ-not_working">{lang.occ_not_working}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Job (conditional) */}
      {jobRequired && (
        <div className="space-y-2">
          <Label className="text-neutral-300">{lang.job}</Label>
          <Input
            value={form.job}
            onChange={(e) => set("job", e.target.value)}
            placeholder={lang.job_ph}
            className={selectClass}
            data-testid="profile-job-input"
          />
        </div>
      )}

      {/* Marital status */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.marital}</Label>
        <Select value={form.marital_status} onValueChange={(v) => set("marital_status", v)}>
          <SelectTrigger className={selectClass} data-testid="profile-marital-select">
            <SelectValue placeholder={lang.select} />
          </SelectTrigger>
          <SelectContent className={contentClass}>
            <SelectItem value="married" className={itemClass} data-testid="profile-marital-married">{lang.married}</SelectItem>
            <SelectItem value="single" className={itemClass} data-testid="profile-marital-single">{lang.single}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Children */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.children}</Label>
        <Select value={form.has_children} onValueChange={(v) => set("has_children", v)}>
          <SelectTrigger className={selectClass} data-testid="profile-children-select">
            <SelectValue placeholder={lang.select} />
          </SelectTrigger>
          <SelectContent className={contentClass}>
            <SelectItem value="yes" className={itemClass} data-testid="profile-children-yes">{lang.yes}</SelectItem>
            <SelectItem value="no" className={itemClass} data-testid="profile-children-no">{lang.no}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Children count + gender (conditional) */}
      {hasKids && (
        <>
          <div className="space-y-2">
            <Label className="text-neutral-300">{lang.children_count}</Label>
            <Select value={form.children_count} onValueChange={(v) => set("children_count", v)}>
              <SelectTrigger className={selectClass} data-testid="profile-children-count-select">
                <SelectValue placeholder={lang.select} />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                {CHILD_COUNTS.map((n) => (
                  <SelectItem key={n} value={n} className={itemClass} data-testid={`profile-children-count-${n}`}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-neutral-300">{lang.children_gender}</Label>
            <Select value={form.children_gender} onValueChange={(v) => set("children_gender", v)}>
              <SelectTrigger className={selectClass} data-testid="profile-children-gender-select">
                <SelectValue placeholder={lang.select} />
              </SelectTrigger>
              <SelectContent className={contentClass}>
                <SelectItem value="boy" className={itemClass} data-testid="profile-children-gender-boy">{lang.cg_boy}</SelectItem>
                <SelectItem value="girl" className={itemClass} data-testid="profile-children-gender-girl">{lang.cg_girl}</SelectItem>
                <SelectItem value="both" className={itemClass} data-testid="profile-children-gender-both">{lang.cg_both}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {/* Gender */}
      <div className="space-y-2">
        <Label className="text-neutral-300">{lang.gender}</Label>
        <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
          <SelectTrigger className={selectClass} data-testid="profile-gender-select">
            <SelectValue placeholder={lang.select} />
          </SelectTrigger>
          <SelectContent className={contentClass}>
            <SelectItem value="male" className={itemClass} data-testid="profile-gender-male">{lang.male}</SelectItem>
            <SelectItem value="female" className={itemClass} data-testid="profile-gender-female">{lang.female}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button
        type="submit"
        className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50"
        disabled={!isValid || saving}
        data-testid="profile-submit-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        {lang[submitLabelKey] || lang.save}
      </Button>
    </form>
  );
}

export { L as PROFILE_LABELS };
